import { useCallback, useEffect, useState } from 'react';
import { vehicleDefectsService, type EquipmentHomeSummary } from '../../../services/vehicleDefectsService';
import { APPROVALS_CHANGED_EVENT } from '../../../components/awaitingApprovalsCount';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UseEquipmentHomeResult {
  summary: EquipmentHomeSummary | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * V3-060 Equipment widget: per service area, defects in the last 24 hours, overdue PMs and
 * overdue greasing. `enabled` follows View Repairs at the call site. Goes through the Home request
 * queue, and refetches when defects change (`notifyCriticalDefectsChanged`).
 */
export function useEquipmentHome(enabled: boolean): UseEquipmentHomeResult {
  const [summary, setSummary] = useState<EquipmentHomeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary(await queueHomeRequest(() => vehicleDefectsService.equipmentHome()));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Could not load equipment counts.'));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChanged = () => fetchSummary();
    window.addEventListener(APPROVALS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(APPROVALS_CHANGED_EVENT, onChanged);
  }, [enabled, fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
}
